import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';

import { Companies, WebserviceResponse, Email, SingleCompany, CrmCompareResponse, CrmSyncRequest } from '../interfaces';
import { catchError, map, retry } from 'rxjs/operators';
import { AlertService, IAlertType } from "./alert.service";
import { restBaseUrl, restOriginUrl } from "../utils/rest-url";


@Injectable({
  providedIn: 'root'
})
export class ContactsStoreService {
  contacts: Companies[] = [];
  contact: Companies[] = [];

  private readonly endpoint = restBaseUrl('/contacts');

  constructor(
      private http: HttpClient,
      private alertDispatcher: AlertService,
  ) {
  }

  private handleError(error: HttpErrorResponse){
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side errors
      errorMessage = `Server-side error code: ${error.status} - message: ${error.message}`;
    }
    // window.alert(errorMessage);

    this.alertDispatcher.alertConfig.message = errorMessage;
    this.alertDispatcher.alertConfig.clrAlertType = IAlertType.DANGER;

    // return throwError(errorMessage); deprecated
    return throwError(() => new Error(errorMessage));
  }

  private mapRmsResponse<T>(res: any): WebserviceResponse<T[]> {
    const data = Array.isArray(res?.data) ? res.data : res?.data ? [res.data] : [];
    const status = (res?.status ?? '').toString().toLowerCase();
    return {
      object: data,
      token: undefined,
      hasErrors: status !== 'success',
      message: res?.message ?? '',
      praefix: undefined,
      errors: [],
      firstError: undefined
    };
  }

  getContacts(): Observable<WebserviceResponse<Companies[]>> {
    return this.http.get<any>(
        this.endpoint + '/get_contacts'
    ).pipe(
        map(res => this.mapRmsResponse<Companies>(res)),
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  getCurrentContacts(phmcount: string) : Observable<WebserviceResponse<Companies[]>> {
    const options = new HttpParams({
      fromObject: {
        count: phmcount
      }
    });
    return this.http.get<WebserviceResponse<Companies[]>>(
        this.endpoint + '/last_updated_contacts',
        { params: options }).pipe(
            retry(3),
            catchError(this.handleError.bind(this))
    );
  }

  getCustomers() : Observable<WebserviceResponse<Companies[]>> {
    return this.http.get<WebserviceResponse<Companies[]>>(
        this.endpoint + '/get_customers').pipe(
            retry(3),
            catchError(this.handleError.bind(this))
    );
  }

  getSuppliers() : Observable<WebserviceResponse<Companies[]>> {
    return this.http.get<WebserviceResponse<Companies[]>>(
        this.endpoint + '/get_supplier').pipe(
            retry(3),
            catchError(this.handleError.bind(this))
    );
  }

  getSingleCompany(cid: number) : Observable<WebserviceResponse<SingleCompany[]>> {
    return this.http.get<any>(
        this.endpoint + `/get_single_contact/${cid}`
    ).pipe(
        map(res => this.mapRmsResponse<SingleCompany>(res)),
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  getCrmCompare(cid: number, sessionId?: string): Observable<CrmCompareResponse> {
    let params = new HttpParams();
    if (sessionId && sessionId.trim().length > 0) {
      params = params.set('session_id', sessionId);
    }
    return this.http.get<CrmCompareResponse>(
      `${this.endpoint}/${cid}/crm_compare`,
      { params }
    ).pipe(
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }

  syncCrmContact(cid: number, payload: CrmSyncRequest): Observable<CrmCompareResponse> {
    return this.http.post<CrmCompareResponse>(
      `${this.endpoint}/${cid}/crm_sync`,
      payload
    ).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  updateCompany(companies: Companies): Observable<any> {
    return this.http.put(
        `${this.endpoint}/update_contact?cid=${companies.cid}`,
        companies,
        { responseType: 'text'}
    ).pipe(
        catchError(this.handleError.bind(this))
    );
  }

  createCompany(companies: Companies) {
    return this.http.post(
        `${this.endpoint}/add_contact`,
        companies,
        { responseType: 'json'}
    ).pipe(
        catchError(this.handleError.bind(this))
    );
  }

  deleteCompany(companyID: number): Observable<any> {
    return this.http.delete(
        `${this.endpoint}/delete_contact?contact_id=${companyID}`,
        { responseType: 'text' }
    ).pipe(
        catchError(this.handleError.bind(this))
    );
  }

  sendEmail(e: Email ) {
    return this.http.post(
        restOriginUrl('/rest/email/send_mail'),
        e,
        { responseType: 'json'}
    ).pipe(
        catchError(this.handleError.bind(this))
    );
  }
}
